import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Trash2, Edit3, Plus, RefreshCw } from 'lucide-react';
import { ADMIN } from '@/constants/testIds/healthspan';
import { toast } from 'sonner';

const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const EMPTY_PROTOCOL = {
  title: '',
  mechanism: 'cellular_senescence',
  intervention_type: 'nutraceutical',
  evidence_level: 'RCT',
  source_url: '',
  source_citation_text: '',
  is_placeholder_citation: false,
  safety_text: '',
  contraindications: [],
  summary_public: '',
  summary_researcher: '',
  evidence_weight: 0.5,
  biomarker_targets: [],
  applicability_rules: [],
};

export default function Admin() {
  const [meta, setMeta] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [editing, setEditing] = useState(null);
  const [weights, setWeights] = useState({ evidence_weight: 0.4, mechanism_weight: 0.3, biomarker_weight: 0.3 });
  const [feedback, setFeedback] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    const [{ data: m }, { data: lst }, { data: w }, { data: fb }] = await Promise.all([
      api.get('/meta'),
      api.get('/protocols?limit=200'),
      api.get('/admin/weights'),
      api.get('/admin/feedback'),
    ]);
    setMeta(m);
    setProtocols(lst.protocols);
    setWeights(w);
    setFeedback(fb.feedback);
  };

  useEffect(() => { load().catch(e => toast.error(e.response?.data?.detail || 'Load failed')); }, []);

  const startEdit = (p) => setEditing(p ? { ...p, contraindications: (p.contraindications || []).join('\n'), biomarker_targets: (p.biomarker_targets || []).join(','), applicability_rules: (p.applicability_rules || []).join(',') } : { ...EMPTY_PROTOCOL });

  const save = async () => {
    if (!editing.title || !editing.source_url || !editing.summary_public) { toast.error('Title, source URL, and public summary are required'); return; }
    const payload = {
      ...editing,
      evidence_weight: Number(editing.evidence_weight),
      contraindications: (typeof editing.contraindications === 'string' ? editing.contraindications.split('\n') : editing.contraindications).map(s => s.trim()).filter(Boolean),
      biomarker_targets: (typeof editing.biomarker_targets === 'string' ? editing.biomarker_targets.split(',') : editing.biomarker_targets).map(s => s.trim()).filter(Boolean),
      applicability_rules: (typeof editing.applicability_rules === 'string' ? editing.applicability_rules.split(',') : editing.applicability_rules).map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editing.id) {
        await api.put(`/admin/protocols/${editing.id}`, payload);
        toast.success('Protocol updated');
      } else {
        await api.post('/admin/protocols', payload);
        toast.success('Protocol created');
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this protocol?')) return;
    try { await api.delete(`/admin/protocols/${id}`); await load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const saveWeights = async () => {
    try { const { data } = await api.put('/admin/weights', weights); setWeights(data); toast.success('Weights saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const refreshTrials = async () => {
    setRefreshing(true);
    try { const { data } = await api.post('/admin/trials/refresh'); toast.success(`Refreshed: ${Object.keys(data.refreshed).length} mechanisms`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Refresh failed'); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-[color:var(--muted-fg)]">Admin</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight">Manage protocols, weights and feedback</h1>
        </div>
        <Button variant="outline" onClick={refreshTrials} disabled={refreshing}><RefreshCw className="mr-2 h-4 w-4" /> {refreshing ? 'Refreshing…' : 'Refresh trials cache'}</Button>
      </div>

      <Tabs defaultValue="protocols">
        <TabsList>
          <TabsTrigger data-testid={ADMIN.protocolsTab} value="protocols">Protocols ({protocols.length})</TabsTrigger>
          <TabsTrigger data-testid={ADMIN.weightsTab} value="weights">Weights</TabsTrigger>
          <TabsTrigger data-testid={ADMIN.feedbackTab} value="feedback">Feedback ({feedback.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="protocols" className="mt-4">
          <div className="mb-3"><Button data-testid={ADMIN.newProtocolBtn} onClick={()=>startEdit(null)} className="bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] hover:bg-[color:var(--brand-primary-2)]"><Plus className="mr-2 h-4 w-4" /> New protocol</Button></div>
          <Card className="rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[color:var(--surface-2)] text-[color:var(--muted-fg)]">
                  <tr><th className="text-left px-3 py-2">Title</th><th className="text-left px-3 py-2">Mechanism</th><th className="text-left px-3 py-2">Evidence</th><th className="text-left px-3 py-2">Weight</th><th className="px-3 py-2"></th></tr>
                </thead>
                <tbody>
                  {protocols.map(p => (
                    <tr key={p.id} className="border-t border-[color:var(--border-color)]">
                      <td className="px-3 py-2 font-medium">{p.title}</td>
                      <td className="px-3 py-2">{labelize(p.mechanism)}</td>
                      <td className="px-3 py-2">{labelize(p.evidence_level)}</td>
                      <td className="px-3 py-2 tabular-nums">{p.evidence_weight}</td>
                      <td className="px-3 py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={()=>startEdit(p)}><Edit3 className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={()=>remove(p.id)} className="text-[color:var(--danger)]"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="weights" className="mt-4">
          <Card data-testid={ADMIN.weightsForm} className="p-6 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] max-w-2xl">
            <div className="text-sm font-semibold">Match weights</div>
            <p className="mt-1 text-xs text-[color:var(--muted-fg)]">These weights apply to the composite ranking score. They are auto-normalised.</p>
            <div className="mt-4 grid sm:grid-cols-3 gap-3">
              {['evidence_weight','mechanism_weight','biomarker_weight'].map(k => (
                <div key={k} className="space-y-1">
                  <Label className="capitalize">{k.replace('_',' ')}</Label>
                  <Input type="number" step="0.05" min="0" max="1" value={weights[k]} onChange={(e)=>setWeights(w=>({...w,[k]:Number(e.target.value)}))} />
                </div>
              ))}
            </div>
            <div className="mt-4"><Button onClick={saveWeights} className="bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] hover:bg-[color:var(--brand-primary-2)]">Save weights</Button></div>
          </Card>
        </TabsContent>

        <TabsContent value="feedback" className="mt-4">
          <Card className="rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] overflow-hidden">
            {feedback.length === 0 ? (
              <div className="p-6 text-sm text-[color:var(--muted-fg)]">No feedback yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[color:var(--surface-2)] text-[color:var(--muted-fg)]">
                  <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Protocol</th><th className="text-left px-3 py-2">Helpful</th><th className="text-left px-3 py-2">Notes</th></tr>
                </thead>
                <tbody>
                  {feedback.map(f => (
                    <tr key={f.id} className="border-t border-[color:var(--border-color)]">
                      <td className="px-3 py-2 text-xs text-[color:var(--muted-fg)]">{new Date(f.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2 text-xs">{f.user_id?.slice(0,8)}</td>
                      <td className="px-3 py-2 text-xs">{f.protocol_id?.slice(0,8)}</td>
                      <td className="px-3 py-2">{f.helpful ? 'Yes' : 'No'}</td>
                      <td className="px-3 py-2 text-xs">{f.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(v)=>!v && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit protocol' : 'New protocol'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div className="space-y-1"><Label>Title</Label><Input value={editing.title} onChange={(e)=>setEditing(p=>({...p,title:e.target.value}))} /></div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1"><Label>Mechanism</Label>
                  <Select value={editing.mechanism} onValueChange={(v)=>setEditing(p=>({...p,mechanism:v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.mechanisms||[]).map(m=><SelectItem key={m} value={m}>{labelize(m)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Intervention type</Label>
                  <Select value={editing.intervention_type} onValueChange={(v)=>setEditing(p=>({...p,intervention_type:v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.intervention_types||[]).map(m=><SelectItem key={m} value={m}>{labelize(m)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Evidence level</Label>
                  <Select value={editing.evidence_level} onValueChange={(v)=>setEditing(p=>({...p,evidence_level:v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.evidence_levels||[]).map(m=><SelectItem key={m} value={m}>{labelize(m)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1"><Label>Source URL (PubMed/clinicaltrials.gov)</Label><Input value={editing.source_url} onChange={(e)=>setEditing(p=>({...p,source_url:e.target.value}))} /></div>
              <div className="space-y-1"><Label>Citation text</Label><Input value={editing.source_citation_text || ''} onChange={(e)=>setEditing(p=>({...p,source_citation_text:e.target.value}))} /></div>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!editing.is_placeholder_citation} onChange={(e)=>setEditing(p=>({...p,is_placeholder_citation:e.target.checked}))} /> Mark as placeholder citation</label>
              <div className="space-y-1"><Label>Public summary</Label><Textarea rows={3} value={editing.summary_public} onChange={(e)=>setEditing(p=>({...p,summary_public:e.target.value}))} /></div>
              <div className="space-y-1"><Label>Researcher summary</Label><Textarea rows={4} value={editing.summary_researcher} onChange={(e)=>setEditing(p=>({...p,summary_researcher:e.target.value}))} /></div>
              <div className="space-y-1"><Label>Safety text</Label><Textarea rows={2} value={editing.safety_text || ''} onChange={(e)=>setEditing(p=>({...p,safety_text:e.target.value}))} /></div>
              <div className="space-y-1"><Label>Contraindications (one per line)</Label><Textarea rows={3} value={editing.contraindications} onChange={(e)=>setEditing(p=>({...p,contraindications:e.target.value}))} /></div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1"><Label>Biomarker targets (CSV)</Label><Input value={editing.biomarker_targets} onChange={(e)=>setEditing(p=>({...p,biomarker_targets:e.target.value}))} placeholder="crp_mg_l, il6_pg_ml" /></div>
                <div className="space-y-1"><Label>Applicability rules (CSV)</Label><Input value={editing.applicability_rules} onChange={(e)=>setEditing(p=>({...p,applicability_rules:e.target.value}))} placeholder="age_gt_50, p16_elevated" /></div>
                <div className="space-y-1"><Label>Evidence weight (0-1)</Label><Input type="number" step="0.05" min="0" max="1" value={editing.evidence_weight} onChange={(e)=>setEditing(p=>({...p,evidence_weight:e.target.value}))} /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button><Button data-testid={ADMIN.saveProtocolBtn} onClick={save} className="bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] hover:bg-[color:var(--brand-primary-2)]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
